/**
 * `05/ Deployed Projects` — what is LIVE, at `/countries/:cc/deployed`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠ THE SCREEN IS NOW TITLED "DEPLOYED WORK", AND THE IDENTIFIERS ARE NOT
 * ═══════════════════════════════════════════════════════════════════════════
 * The word "project" on this screen always meant "a CRF that has been deployed"
 * — the design's own noun for a unit of work, chosen before there was such a
 * thing as a `work_type='project'` record. Migration 37 gave the word a SECOND,
 * narrower meaning, and this register contains both kinds: its read has no
 * predicate that could exclude a project (design §5.2a).
 *
 * A screen literally called "Deployed Projects" that lists CRFs is a defect a
 * disambiguating line cannot fix, because the title is what a reader takes away.
 * So the TITLE changes and the SUBTITLE's noun with it.
 *
 * ⚠ THE ROW TYPE, THE ROUTE, THE WIRE FIELDS AND THE QUERY KEYS DO NOT CHANGE —
 * `DeployedProjectRow` keeps its name, `/countries/:cc/deployed` keeps its path.
 * That is design D-8 held exactly: renaming a shipped identifier to resolve a
 * reader's ambiguity buys nothing and costs a migration of every reference. The
 * ambiguity is a COPY problem and it is fixed in copy.
 *
 * ===========================================================================
 * ONE ROW PER CRF, AND THE COUNT COLUMN COUNTS SOMETHING ELSE
 * ===========================================================================
 * The sibling Deployment register is one row per DEPLOYMENT. This one is one row
 * per CRF, and it carries `deployment_count` — how many times THAT CRF has been
 * deployed. Two numbers, two subjects, on two screens that sit next to each other
 * in the sidebar, and a reader who mixes them up gets a wrong answer that looks
 * right. So the words do the disambiguating and nothing is left to the number:
 *
 *   · the page subtitle counts DEPLOYED PROJECTS (the server's `total`, which is
 *     the row count, which is the CRF count);
 *   · the column is headed DEPLOYMENTS and every cell repeats the unit —
 *     "3 deployments", never a bare "3" that could be read as three projects.
 *
 * ===========================================================================
 * EVERY `last_*` FIELD IS NULLABLE AND EVERY ABSENCE IS SPELLED OUT
 * ===========================================================================
 * `last_deployed_at` is null when a CRF reached 'deployed'/'signed_off' without a
 * deployment row ever being written. That is a real state and it is rendered as a
 * sentence: a blank cell would read as a rendering failure, which is the one thing
 * an operator cannot act on.
 *
 * `last_deployed_by` comes from a SEPARATE subselect, so the wire contract lets it
 * be null while the timestamp is not — and this screen renders that case rather
 * than deriving one absence from the other. HONESTLY, THOUGH: it cannot happen
 * today. `fk_deployments_deployed_by` is ON DELETE RESTRICT (migration 6), so a
 * user who has deployed cannot be deleted, and the name is null exactly when the
 * timestamp is. The branch is defence against the API's SHAPE, not a state the
 * current schema can produce, and saying otherwise would be inventing a story for
 * a cell nobody has seen. What is NOT conditional is the rule it keeps: no other
 * name — not the customer's, not another row's deployer — may stand in, which is
 * what the DL file register's "Unknown" pill exists to refuse.
 *
 * ===========================================================================
 * NO FILTER CONTROLS
 * ===========================================================================
 * The route accepts `?party=` and this screen sets it never: the design draws no
 * control for it, and a filter only a hand-typed URL can set is a filter nobody
 * can clear. The register is therefore honestly unfiltered — every live CRF in
 * the country, ordered by customer name, which is what the header says.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { WorkTypeBadge } from '../crfs/WorkTypeBadge';
import { CRF_STAGES } from '../crfs/domain';
import { deploymentKeys, fetchDeployedProjects, registerErrorText } from './deployment-api';
import type { DeployedProjectRow, DeployedProjectsFilters } from './deployment-api';

const PER_PAGE = 50;

/**
 * The kind tone pairs again, file-private — see `DeploymentRegisterPage`'s note
 * on why the map is copied rather than shared. Same rule: an unrecognised kind
 * keeps the server's own word on a neutral chip.
 */
const KIND_PILL: Readonly<Record<string, { label: string; classes: string }>> = {
  deploy: { label: 'Deploy', classes: 'bg-ok-bg text-ok-fg' },
  redeploy: { label: 'Redeploy', classes: 'bg-warn-bg text-warn-fg' },
};

function kindPill(kind: string): { label: string; classes: string } {
  return KIND_PILL[kind] ?? { label: kind, classes: 'bg-subtle text-fg ring-1 ring-line' };
}

/**
 * `Pill/Stage` — the CRF status tones, re-authored from `CrfsListPage`'s
 * file-private `CrfStagePill`. The vocabulary itself is NOT re-authored: it comes
 * from `crfs/domain.ts`, the one place a `crfs.status` gets a word, so a wording
 * change stays one edit. This route narrows the status to 'deployed' |
 * 'signed_off', but an unknown value still renders the server's own word on a
 * neutral chip rather than a blank pill or a guessed colour.
 */
const STAGE_PILL_CLASSES: Readonly<Record<string, string>> = {
  violet: 'bg-stage-confirm-bg text-stage-confirm-fg',
  amber: 'bg-warn-bg text-warn-fg',
  blue: 'bg-info-bg text-info-fg',
  green: 'bg-ok-bg text-ok-fg',
  slate: 'bg-subtle text-fg ring-1 ring-line',
  red: 'bg-stage-lost-bg text-stage-lost-fg',
};

function StatusPill({ status }: { status: string }): React.JSX.Element {
  const descriptor = CRF_STAGES[status];
  const tone = descriptor?.tone ?? 'slate';
  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        STAGE_PILL_CLASSES[tone] ?? STAGE_PILL_CLASSES.slate,
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {descriptor?.label ?? status}
    </span>
  );
}

export function DeployedProjectsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters: DeployedProjectsFilters = { page, perPage: PER_PAGE };

  const list = useQuery({
    queryKey: deploymentKeys.deployedProjects(countryCode, filters),
    queryFn: ({ signal }) => fetchDeployedProjects(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — a designed refusal that names who to ask and never the permission key.
    // Never an empty table, which would read as "nothing is live".
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Deployed work is not available to this account">
          Viewing the deployed-work register in {countryCode} needs the CRF permission. Ask
          an administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Deployed Work"
        subtitle={
          list.data === undefined
            ? undefined
            : // RECORDS, from the SERVER's total — one row is one CRF, which may be
              // CRF work or project work. The per-row DEPLOYMENTS column counts
              // something else entirely, which is why both units are spelled out.
              `${String(list.data.total)} ${
                list.data.total === 1 ? 'deployed record' : 'deployed records'
              }`
        }
      />

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the deployed-work register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The deployed-work register could not be loaded">
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes-list V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)}{' '}
            {total === 1 ? 'deployed record' : 'deployed records'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState title="No CRF in this country has been deployed yet." />
        </div>
      ) : (
        <DeployedProjectsTable countryCode={countryCode} rows={rows} />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

function DeployedProjectsTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: readonly DeployedProjectRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1180px] border-collapse text-left">
        <caption className="sr-only">
          Every deployed CRF in {countryCode}, one row per CRF
        </caption>
        <colgroup>
          <col className="w-[170px]" />
          <col />
          <col className="w-[150px]" />
          <col className="w-[230px]" />
          <col className="w-[160px]" />
          <col className="w-[150px]" />
          <col className="w-[130px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CRF
            </th>
            {/* Server-ordered by customer name — stated, not a dead sort control. */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CUSTOMER <span aria-hidden="true">↑</span>
              <span className="sr-only">, A to Z</span>
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              STATUS
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              LAST DEPLOYMENT
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              LAST DEPLOYED BY
            </th>
            {/*
             * DEPLOYMENTS, not projects. The row IS the project; this column is
             * how many times it went out.
             */}
            <th scope="col" className="px-3 text-right text-th uppercase text-fg-muted">
              DEPLOYMENTS
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.crf_id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
              <td className="px-3 py-3 align-middle">
                {/*
                  The badge is the other half of the title change: "Deployed Work" says the
                  register holds both kinds, and this says WHICH kind each row is. A title
                  that widened without the rows saying which is which would have replaced a
                  wrong word with a vague one.
                */}
                <span className="flex items-center gap-2">
                  <Link
                    to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                    className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    {row.crf_doc_no}
                  </Link>
                  <WorkTypeBadge workType={row.work_type} />
                </span>
              </td>

              <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

              <td className="px-3 py-3 align-middle">
                <StatusPill status={row.status} />
              </td>

              <LastDeploymentCell at={row.last_deployed_at} kind={row.last_kind} />

              {/*
               * BRANCH ON THE DEPLOYMENT, NOT ON THE NAME — the two absences have
               * different causes and only one of them can happen.
               *
               * `deployments.deployed_by` is NOT NULL with ON DELETE RESTRICT
               * (migration 6), and the service's `last_deployed_by` subselect INNER
               * JOINs users, so a null name means there is no deployment row at all
               * — never that an account was deleted, which the schema forbids.
               *
               * ⚠ THIS CELL SAID THE WRONG THING. It rendered "Unknown" captioned
               * "No deployment names an account that still exists", which names a
               * cause that cannot occur, on the only case that can render. A
               * tooltip explaining an impossible cause is worse than none: it sends
               * whoever reads it looking for a deleted user who never existed.
               *
               * The `at !== null && by === null` branch is kept because the DTO
               * still types both independently — if the FK is ever relaxed, this
               * says the honest thing on day one instead of inheriting a wrong
               * sentence.
               */}
              <td className="px-3 py-3 align-middle">
                {row.last_deployed_at === null ? (
                  <span
                    title="This CRF's status was set without a deployment row, so no one is recorded as having deployed it."
                    className="text-label text-fg-muted"
                  >
                    No deployment recorded
                  </span>
                ) : row.last_deployed_by === null ? (
                  <span
                    // Never another row's name, and never the customer's: the DL
                    // file register's rule, for the same reason.
                    title="A deployment is recorded but the account that made it can no longer be resolved."
                    className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg-muted ring-1 ring-line"
                  >
                    Unknown
                  </span>
                ) : (
                  <span className="text-body13 text-fg">{row.last_deployed_by}</span>
                )}
              </td>

              <td className="tnum px-3 py-3 text-right align-middle text-body13 text-fg-secondary">
                {/* The unit is repeated in every cell on purpose — see the header. */}
                {String(row.deployment_count)}{' '}
                {row.deployment_count === 1 ? 'deployment' : 'deployments'}
              </td>

              <td className="px-3 py-3 align-middle">
                <Link
                  to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                  className="whitespace-nowrap text-body13 font-medium text-indigo hover:underline"
                >
                  Open CRF
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The latest deployment — its kind and when it happened, or a sentence saying
 * there is no deployment to describe.
 *
 * `last_kind === 'redeploy'` is rendered AS a redeploy. A register that showed
 * only "deployed" would hide that this customer's software went out more than
 * once, which is the fact somebody reading a live-systems list most needs.
 */
function LastDeploymentCell({
  at,
  kind,
}: {
  at: string | null;
  kind: string | null;
}): React.JSX.Element {
  if (at === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          title="This CRF's status was set without a deployment row, so there is no deployment to describe."
          className="text-label text-fg-muted"
        >
          No deployment recorded
        </span>
      </td>
    );
  }

  const pill = kind === null ? null : kindPill(kind);
  return (
    <td className="px-3 py-3 align-middle">
      <div className="flex flex-col gap-1">
        {pill === null ? null : (
          <span
            data-last-kind={kind}
            className={cn(
              'inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
              pill.classes,
            )}
          >
            <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
            {pill.label}
          </span>
        )}
        <span className="text-body13 text-fg">{formatDateLong(at)}</span>
        <span className="text-label text-fg-muted">{relativeTime(at)}</span>
      </div>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored per screen, the house pattern every list in
 * this app follows: the geometry is shared, the component is file-private.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
